import { useEffect, useState } from 'react';

import { InfiniteScroll, VerticalContainer, Wrapper } from '@american-autonomy/ag-ui';

import UsersGridHeader from 'components/UsersGrid/components/UsersGridHeader';
import WithArrow from 'components/WithArrow';
import WithResize from 'components/WithResize';
import GridText from 'components/text/GridText';

import useGridTablesResizeObserver from 'hooks/useGridTablesResizeObserver';

import useTranslation from './hooks/useTranslation';

import UsersGridRow from './components/UsersGridRow';
import { TEST_ID } from './constants';
import type { FunctionComponent, Props } from './types';

import styles from './styles.module.scss';

const UsersGrid: FunctionComponent<Props> = ({
    className = '',
    onNext,
    rows = [],
    testId = TEST_ID
}) => {
    const { CREATED_AT, LOADING, NAME, NO_MORE_USERS, NO_USERS, ROLES, STATUS } = useTranslation();

    const { containerRef, handleResize } = useGridTablesResizeObserver('--users-grid-row-columns');
    const [hasNextPage, setHasNextPage] = useState<boolean>(false);
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
        if (!initialized) {
            const hasNext = typeof onNext === 'function';

            setHasNextPage(hasNext);

            if (hasNext) {
                setInitialized(true);
            }
        }
    }, [initialized, onNext]);

    const fetchNext = async () => {
        if (!hasNextPage || !onNext) {
            return;
        }

        const { done } = await onNext();

        setHasNextPage(!done);
    };

    const endingMessage = initialized && rows.length === 0 ? NO_USERS : NO_MORE_USERS;

    return (
        <VerticalContainer
            className={`${className} ${styles.usersGrid}`}
            ref={containerRef}
            testId={testId}>
            <UsersGridHeader>
                <WithResize onResize={handleResize}>
                    <WithArrow className={styles.withArrow}>
                        <GridText text={NAME} />
                    </WithArrow>
                </WithResize>

                <WithResize onResize={handleResize}>
                    <GridText text={ROLES} />
                </WithResize>

                <WithResize onResize={handleResize}>
                    <GridText text={STATUS} />
                </WithResize>

                <WithResize onResize={handleResize}>
                    <GridText text={CREATED_AT} />
                </WithResize>
            </UsersGridHeader>

            <InfiniteScroll
                endingMessage={
                    <Wrapper className={styles.wrapper}>
                        <GridText text={endingMessage} />
                    </Wrapper>
                }
                loader={
                    <Wrapper className={styles.wrapper}>
                        <GridText text={LOADING} />
                    </Wrapper>
                }
                className={styles.infiniteScroll}
                fetchNextPage={fetchNext}
                hasNextPage={hasNextPage}>
                {rows.map(({ createdAt, firstName, id, lastName, roles, status }) => (
                    <UsersGridRow
                        createdAt={createdAt}
                        firstName={firstName}
                        id={id}
                        key={id}
                        lastName={lastName}
                        roles={roles}
                        status={status}
                    />
                ))}
            </InfiniteScroll>
        </VerticalContainer>
    );
};

export default UsersGrid;
